/**
 * V86 — Lightweight searchable dropdown (Select2/React-Select flavour) built
 * on native React state + Tailwind. Zero runtime deps, ~130 LoC, fully
 * keyboard-navigable and mobile-friendly.
 *
 * Props:
 *   • options: Array<{ value: string, label: string, meta?: ReactNode, keywords?: string }>
 *   • value:   currently-selected value (string) or ""
 *   • onChange: (value) => void
 *   • placeholder, disabled, data-testid, ariaLabel, className
 *   • renderSelected(option) → ReactNode custom trigger content (optional)
 *   • renderOption(option, {active}) → ReactNode custom row content (optional)
 *   • emptyText: shown when no filter match (default "No results")
 *
 * Notes:
 *   • Search is case-insensitive; matches option.label + option.keywords.
 *   • Enter selects, Esc closes, ArrowUp/Down navigate, Home/End jump.
 *   • Popup is portalled visually via `absolute` + z-index (no react-portal
 *     needed — parent form container is inside <form>, not a modal).
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronDown, X, Search, Check } from "lucide-react";

export default function SearchableSelect({
    options,
    value,
    onChange,
    placeholder = "Select…",
    disabled = false,
    className = "",
    "data-testid": testId,
    ariaLabel,
    renderSelected,
    renderOption,
    emptyText = "No results",
    clearable = false,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const rootRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    const selected = options.find((o) => o.value === value) || null;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => {
            const hay = `${o.label} ${o.keywords || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [options, query]);

    // Reset active row when filter changes / opens
    useEffect(() => {
        setActiveIdx(0);
    }, [query, open]);

    // Focus the search input on open (desktop only — mobile browsers
    // may pop the software keyboard which we do want, so we still focus).
    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 20);
    }, [open]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    // Scroll active row into view
    useEffect(() => {
        if (!open || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
        if (el) el.scrollIntoView({ block: "nearest" });
    }, [activeIdx, open]);

    const pick = useCallback((opt) => {
        onChange(opt.value);
        setOpen(false);
        setQuery("");
    }, [onChange]);

    const onKeyDown = (e) => {
        if (!open && (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ")) {
            e.preventDefault();
            setOpen(true);
            return;
        }
        if (!open) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === "Home") { e.preventDefault(); setActiveIdx(0); }
        else if (e.key === "End") { e.preventDefault(); setActiveIdx(filtered.length - 1); }
        else if (e.key === "Enter") { e.preventDefault(); if (filtered[activeIdx]) pick(filtered[activeIdx]); }
        else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    };

    return (
        <div ref={rootRef} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                onKeyDown={onKeyDown}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid={testId}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm bg-white border rounded-lg outline-none transition-all
                    ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-rose-300"}
                    ${open ? "border-rose-500 ring-1 ring-rose-500" : "border-slate-200"}`}
            >
                <span className={`min-w-0 truncate flex items-center gap-2 ${!selected ? "text-slate-400" : "text-slate-900"}`}>
                    {selected ? (renderSelected ? renderSelected(selected) : selected.label) : placeholder}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                    {clearable && selected && !disabled && (
                        <span
                            role="button"
                            tabIndex={-1}
                            aria-label="Clear selection"
                            onClick={(e) => { e.stopPropagation(); onChange(""); }}
                            className="p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700"
                        >
                            <X className="w-3.5 h-3.5" />
                        </span>
                    )}
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>
            {open && (
                <div
                    className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 flex flex-col overflow-hidden"
                    data-testid={testId ? `${testId}-panel` : undefined}
                >
                    <div className="p-2 border-b border-slate-100 flex items-center gap-2">
                        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Type to search…"
                            data-testid={testId ? `${testId}-search` : undefined}
                            className="w-full text-sm outline-none placeholder:text-slate-400 bg-transparent"
                        />
                    </div>
                    <div ref={listRef} role="listbox" className="overflow-y-auto flex-1">
                        {filtered.length === 0 && (
                            <div className="px-3 py-6 text-center text-xs text-slate-500">{emptyText}</div>
                        )}
                        {filtered.map((o, i) => {
                            const active = i === activeIdx;
                            const isSelected = o.value === value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    role="option"
                                    aria-selected={isSelected}
                                    data-idx={i}
                                    data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onClick={() => pick(o)}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors
                                        ${active ? "bg-rose-50" : "hover:bg-slate-50"}
                                        ${isSelected ? "text-rose-700 font-semibold" : "text-slate-800"}`}
                                >
                                    <span className="flex-1 min-w-0 truncate">
                                        {renderOption ? renderOption(o, { active }) : o.label}
                                    </span>
                                    {isSelected && <Check className="w-3.5 h-3.5 shrink-0 text-rose-600" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
